import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {map, mergeMap, of, switchMap} from 'rxjs';
import {catchError, exhaustMap, filter} from 'rxjs/operators';
import {CatalogActions} from '@store/catalog/catalog.actions';
import {CatalogService} from '@store/catalog/catalog.service';
import {noAuthWhenRouterHasNavigatedTo, skipUnless, whenRouterHasNavigatedTo, withLatestFromSelectors} from '@store/common/effects.helpers';
import {openConfirmationDialog} from '@aviary/store/common/effect-dialog.helpers';
import {DeleteFavoriteDialog} from '@app/my-account/favorites/delete-favorite/delete-favorite.dialog';
import {RouterSelectors} from '@store/aviary/aviary.adapter';
import {isDefined} from '@aviary/store/common/typing.helpers';
import {CatalogSelectors} from '@store/catalog/catalog.selectors';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {PatronActions} from '@store/patron/patron.actions';
import {HoldsActions} from '@store/holds/holds.actions';
import {concatLatestFrom} from '@ngrx/operators';
import {Store} from '@ngrx/store';
import {createFilterParamUpdateActionFor, createFilterQueryParamNameFrom, filterOptionsWithSelectedFirstFor} from '@store/catalog/catalog.helpers';
import {AllCatalogFiltersDialog} from '@app/catalog/search/catalog-filters/all-catalog-filters/all-catalog-filters.dialog';

@Injectable({providedIn: 'root'})
export class CatalogEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly service: CatalogService = inject(CatalogService);
  private readonly store: Store = inject(Store);

  public loadCatalogRecordOnRouterNavigatedToBibDetails = createEffect(() =>
    this.actions.pipe(
      noAuthWhenRouterHasNavigatedTo('CATALOG_BIB_DETAILS'),
      withLatestFromSelectors(RouterSelectors.paramValue('id'), CatalogSelectors.Current.id),
      filter(([, idParam, currentId]) => isDefined(idParam) && Number(idParam) !== currentId),
      mergeMap(([, idParam]) => [
        CatalogActions.loadCatalogRecord(Number(idParam)),
        CatalogActions.loadCatalogAvailability(Number(idParam)),
        CatalogActions.loadCatalogRelatedItems(Number(idParam)),
        HoldsActions.loadHoldsForCatalogRecords([Number(idParam)])
      ]),
    )
  );

  public loadCatalogRecord = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadCatalogRecord),
      switchMap(action => this.service.loadCatalogRecord(action.id).pipe(
        map(response => CatalogActions.loadCatalogRecordSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadCatalogRecordFailure(error)))
      ))
    )
  );

  public loadCatalogAvailability = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadCatalogAvailability),
      switchMap(action => this.service.loadCatalogAvailability(action.id).pipe(
        map(response => CatalogActions.loadCatalogAvailabilitySuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadCatalogAvailabilityFailure(error)))
      ))
    )
  );

  public loadCatalogRelatedItems = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadCatalogRelatedItems),
      switchMap(action => this.service.loadCatalogRelatedItems(action.id).pipe(
        map(response => CatalogActions.loadCatalogRelatedItemsSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadCatalogRelatedItemsFailure(error)))
      ))
    )
  );

  public loadCatalogCarouselsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      noAuthWhenRouterHasNavigatedTo('CATALOG'),
      map(() => CatalogActions.loadCatalogCarousels())
    )
  );

  public searchCatalogOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      noAuthWhenRouterHasNavigatedTo('CATALOG_SEARCH'),
      map(() => CatalogActions.searchCatalog())
    )
  );

  public searchCatalog = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.searchCatalog),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.searchCatalogRecords(queryParams).pipe(
        map(response => CatalogActions.searchCatalogSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.searchCatalogFailure(error)))
      ))
    )
  );

  public loadHoldDataOnSearchCatalogSuccess = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.searchCatalogSuccess),
      withLatestFromSelectors(CatalogSelectors.List.recordIds),
      map(([, recordIds]) => HoldsActions.loadHoldsForCatalogRecords(recordIds))
    )
  );

  public loadCatalogCarousels = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadCatalogCarousels),
      switchMap(() => this.service.getCatalogCarousels().pipe(
        map(response => CatalogActions.loadCatalogCarouselsSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadCatalogCarouselsFailure(error)))
      ))
    )
  );

  public loadFavoritesOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('FAVORITES'),
      map(() => CatalogActions.loadFavorites())
    )
  );

  public loadFavorites = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadFavorites),
      withLatestFromSelectors(RouterSelectors.queryParams),
      switchMap(([, queryParams]) => this.service.getFavorites(queryParams).pipe(
        map(response => CatalogActions.loadFavoritesSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadFavoritesFailure(error)))
      ))
    )
  );

  public loadFavoritesIdsOnRouterNavigated = createEffect(() =>
    this.actions.pipe(
      whenRouterHasNavigatedTo('CATALOG_BIB_DETAILS', 'FAVORITES', 'BORROWING_HISTORY'),
      withLatestFromSelectors(PatronSelectors.Auth.hasPatron),
      filter(([, hasPatron]) => hasPatron),
      map(() => CatalogActions.loadFavoritesIds())
    )
  );

  public loadFavoritesOnLogin = createEffect(() =>
    this.actions.pipe(
      ofType(PatronActions.loginPatronSuccess),
      withLatestFromSelectors(RouterSelectors.routeName),
      filter(([, routeName]) => routeName === 'CATALOG_BIB_DETAILS'),
      map(() => CatalogActions.loadFavoritesIds())
    )
  );

  public loadFavoritesIds = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadFavoritesIds),
      switchMap(() => this.service.getFavoritesIds().pipe(
        map(response => CatalogActions.loadFavoritesIdsSuccess(response)),
        catchError((error: unknown) => of(CatalogActions.loadFavoritesIdsFailure(error)))
      ))
    )
  );

  public addFavorite = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.addFavorite),
      exhaustMap(({catalogRecordId}) => this.service.addFavorite(catalogRecordId).pipe(
        map(() => CatalogActions.loadFavoritesIds()),
        catchError((error: unknown) => of(CatalogActions.addFavoriteFailure(error)))
      ))
    )
  );

  // Get the full catalog record object via the catalogRecordId, then start the Remove Favorite process
  public removeFavoriteByCatalogRecordId = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.removeFavoriteByCatalogRecordId),
      switchMap(({catalogRecordId}) => this.service.loadCatalogRecord(catalogRecordId).pipe(
        map(catalogRecord => CatalogActions.showRemoveFavorite(catalogRecord, true)),
        catchError((error: unknown) => of(CatalogActions.removeFavoriteFailure(error)))
      ))
    )
  );

  public showRemoveFavorite = createEffect(
    () => this.actions.pipe(
      ofType(CatalogActions.showRemoveFavorite),
      openConfirmationDialog(({favorite, reloadIdsOnly}) => ({
        componentClass: DeleteFavoriteDialog,
        data: {favorite},
        onConfirm: () => CatalogActions.removeFavorite(favorite.id, reloadIdsOnly)
      }))
    )
  );

  public removeFavorite = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.removeFavorite),
      exhaustMap(({catalogRecordId, reloadIdsOnly}) => this.service.removeFavorite(catalogRecordId).pipe(
        map(() => reloadIdsOnly ? CatalogActions.loadFavoritesIds() : CatalogActions.loadFavorites()),
        catchError((error: unknown) => of(CatalogActions.removeFavoriteFailure(error)))
      ))
    )
  );

  public loadHoldsForFavorites = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.loadFavoritesSuccess),
      skipUnless(RouterSelectors.routeNameMatches('FAVORITES')),
      withLatestFromSelectors(CatalogSelectors.Favorites.ids),
      map(([, ids]) => HoldsActions.loadHoldsForCatalogRecords(ids))
    )
  );

  public showAllFilterOptions = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogActions.showAllFilterOptions),
      concatLatestFrom(({filterName}) =>
        this.store.select(filterOptionsWithSelectedFirstFor(filterName))
      ),
      openConfirmationDialog(([{filterName, filterDisplayName}, options]) => ({
        componentClass: AllCatalogFiltersDialog,
        data: {filterDisplayName, options},
        onConfirm: (selectedValues: string[]) =>
          createFilterParamUpdateActionFor(createFilterQueryParamNameFrom(filterName), selectedValues)
      }))
    )
  );
}
